/* eslint-disable lingui/no-unlocalized-strings -- cabeçalhos e verbos HTTP, não texto de UI */
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import Qs from 'qs';

import type { RuntimeConfig } from '@/shared/config';
import { startSpan, templatizePath, type SpanHandle } from '@/shared/telemetry';

import { ApiError } from './api-error';
import { parseBlobError } from './parse-blob-error';
import {
  API_URL_KEYS,
  DEFAULT_TIMEOUT_MS,
  GEO_TRACKED_APIS,
  GEO_TRACKED_METHODS,
  type ApiName,
} from './apis';
import { GEO_HEADER, getCachedGeoLatLong, primeGeoLatLong } from './client-geo';
import { STEP_UP_HEADER, takeStepUpToken, type StepUpGrant } from './step-up-grant';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * `bearer` (padrão): access token do Cognito. `empty-bearer`: `Authorization: Bearer ` vazio, como
     * o `POST /Auth/StartAuth` do legado. `none`: sem header.
     */
    authorization?: 'bearer' | 'empty-bearer' | 'none';
    /**
     * Põe `tenant` (correspondente selecionado) na query. Desligado por padrão: na CreditAPI o
     * `?tenant=` é impersonation, e o legado só manda onde o serviço pede (discovery/03 §1.5).
     */
    withTenant?: boolean;
    /** Caminho sem id para a telemetria, quando o automático (`templatizePath`) não basta. */
    routeTemplate?: string;
    /**
     * Autorização de uso único do `POST /Auth/StepUp/Verify`: vira `X-UY3-StepUp` só nesta chamada e não vale
     * para outra (a CreditAPI a consome). Ausente, o cabeçalho não sai.
     */
    stepUp?: StepUpGrant;
  }
}

export type ApiRequestConfig = AxiosRequestConfig;

export interface HttpRuntime {
  baseUrls: Partial<Record<ApiName, string | null | undefined>>;
  /** Access token corrente (renovado pelo Cognito); `undefined` sem sessão. */
  getAccessToken: () => Promise<string | undefined> | string | undefined;
  /** Correspondente escolhido no seletor da casca; `undefined` sem seleção. */
  getCurrentTenant: () => string | undefined;
  /** Avisa a troca de correspondente (store da casca); sem ele, `useCurrentTenant` não reage. */
  subscribeCurrentTenant?: (onChange: () => void) => () => void;
  /** `X-UY3-Client-Geo` nas mutações; desligado até o Compliance decidir. */
  clientGeo?: boolean;
  /** Troca o adaptador do axios (testes). */
  adapter?: AxiosRequestConfig['adapter'];
}

let runtime: HttpRuntime = {
  baseUrls: {},
  getAccessToken: () => undefined,
  getCurrentTenant: () => undefined,
};
const instances = new Map<ApiName, AxiosInstance>();

/** Liga a camada HTTP no boot: URLs da config, token e tenant. Recria as instâncias. */
export function configureHttp(next: HttpRuntime) {
  runtime = next;
  instances.clear();
}

/** URLs base das cinco APIs a partir da config de runtime. */
export function baseUrlsFromConfig(config: RuntimeConfig): HttpRuntime['baseUrls'] {
  const urls: HttpRuntime['baseUrls'] = {};
  for (const [api, key] of Object.entries(API_URL_KEYS) as [ApiName, keyof RuntimeConfig][]) {
    const value = config[key];
    urls[api] = typeof value === 'string' ? value : undefined;
  }
  return urls;
}

/** O correspondente selecionado, como a casca informou. */
export function getCurrentTenant(): string | undefined {
  return runtime.getCurrentTenant();
}

/** Assina a troca de correspondente na casca; devolve o cancelamento. */
export function subscribeCurrentTenant(onChange: () => void): () => void {
  return runtime.subscribeCurrentTenant?.(onChange) ?? (() => undefined);
}

/** Mesma serialização do legado: arrays repetem a chave (`status=A&status=B`). */
export const serializeParams = (params: unknown) => Qs.stringify(params, { arrayFormat: 'repeat' });

const spans = new WeakMap<InternalAxiosRequestConfig, SpanHandle>();

function hasOwnTenant(params: unknown) {
  return typeof params === 'object' && params !== null && Object.hasOwn(params, 'tenant');
}

async function applyRequestPolicy(api: ApiName, config: InternalAxiosRequestConfig) {
  const mode = config.authorization ?? 'bearer';
  if (mode !== 'none' && !config.headers.has('Authorization')) {
    if (mode === 'empty-bearer') {
      config.headers.set('Authorization', 'Bearer ');
    } else {
      const token = await runtime.getAccessToken();
      if (token) config.headers.set('Authorization', `Bearer ${token}`);
    }
  }

  if (config.stepUp) {
    const token = takeStepUpToken(config.stepUp);
    if (token) config.headers.set(STEP_UP_HEADER, token);
  }

  if (config.withTenant && !hasOwnTenant(config.params)) {
    const tenant = runtime.getCurrentTenant();
    // O legado monta `{ tenant, ...filtros }`: o tenant vem primeiro na query.
    if (tenant)
      config.params = { tenant, ...(config.params as Record<string, unknown> | undefined) };
  }

  const method = config.method?.toLowerCase();
  if (
    runtime.clientGeo &&
    GEO_TRACKED_APIS.includes(api) &&
    method &&
    GEO_TRACKED_METHODS.includes(method)
  ) {
    const latLong = getCachedGeoLatLong();
    if (latLong) config.headers.set(GEO_HEADER, latLong);
    else void primeGeoLatLong();
  }

  const route = config.routeTemplate ?? templatizePath(config.url ?? '');
  const verb = (config.method ?? 'get').toUpperCase();
  spans.set(
    config,
    startSpan(
      'http',
      `${verb} ${route}`,
      { 'uyzy.api': api, 'url.template': route, 'http.request.method': verb },
      { client: true },
    ),
  );
  return config;
}

function endSpan(
  config: InternalAxiosRequestConfig | undefined,
  status: number | undefined,
  errorType?: string,
) {
  if (!config) return;
  const span = spans.get(config);
  if (!span) return;
  spans.delete(config);
  const failed = errorType !== undefined || (status !== undefined && status >= 500);
  span.end(
    {
      'http.response.status_code': status,
      'error.type': errorType ?? (failed ? String(status) : undefined),
    },
    failed ? { code: 'error' } : undefined,
  );
}

/** O token já foi usado; tirado da config para não seguir no `cause` do `ApiError` nem em quem registrar a resposta. */
function forgetStepUp(config: InternalAxiosRequestConfig | undefined) {
  config?.headers.delete(STEP_UP_HEADER);
}

function createInstance(api: ApiName): AxiosInstance {
  const baseURL = runtime.baseUrls[api] ?? '';
  const instance = axios.create({
    baseURL,
    responseType: 'json',
    timeout: DEFAULT_TIMEOUT_MS,
    paramsSerializer: { serialize: serializeParams },
    ...(runtime.adapter ? { adapter: runtime.adapter } : {}),
  });

  instance.interceptors.request.use(async (config) => {
    if (!baseURL && !/^https?:\/\//i.test(config.url ?? '')) {
      throw new ApiError({
        kind: 'unknown',
        api,
        payload: { type: 'error', code: 'API_URL_NOT_CONFIGURED' },
      });
    }
    return applyRequestPolicy(api, config);
  });

  instance.interceptors.response.use(
    (response) => {
      endSpan(response.config, response.status);
      forgetStepUp(response.config);
      return response;
    },
    async (error: unknown) => {
      if (axios.isAxiosError(error)) forgetStepUp(error.config);
      await parseBlobError(error);
      if (axios.isAxiosError(error))
        endSpan(
          error.config,
          error.response?.status,
          error.response ? undefined : (error.code ?? 'network'),
        );
      return Promise.reject(ApiError.from(error, api));
    },
  );
  return instance;
}

function instanceFor(api: ApiName): AxiosInstance {
  let instance = instances.get(api);
  if (!instance) {
    instance = createInstance(api);
    instances.set(api, instance);
  }
  return instance;
}

/** Cliente de uma API legada. Os atalhos devolvem o corpo; `request` devolve a resposta inteira. */
export interface ApiClient {
  readonly api: ApiName;
  request<T>(config: ApiRequestConfig): Promise<AxiosResponse<T>>;
  get<T>(url: string, config?: ApiRequestConfig): Promise<T>;
  delete<T = void>(url: string, config?: ApiRequestConfig): Promise<T>;
  post<T = unknown>(url: string, body?: unknown, config?: ApiRequestConfig): Promise<T>;
  put<T = void>(url: string, body?: unknown, config?: ApiRequestConfig): Promise<T>;
  patch<T = void>(url: string, body?: unknown, config?: ApiRequestConfig): Promise<T>;
}

export function createApiClient(api: ApiName): ApiClient {
  const request = <T>(config: ApiRequestConfig) => instanceFor(api).request<T>(config);
  const data = async <T>(config: ApiRequestConfig) => (await request<T>(config)).data;
  return {
    api,
    request,
    get: (url, config) => data({ ...config, url, method: 'get' }),
    delete: (url, config) => data({ ...config, url, method: 'delete' }),
    post: (url, body, config) => data({ ...config, url, method: 'post', data: body }),
    put: (url, body, config) => data({ ...config, url, method: 'put', data: body }),
    patch: (url, body, config) => data({ ...config, url, method: 'patch', data: body }),
  };
}

export const creditApi = createApiClient('CreditApi');
export const billingApi = createApiClient('BillingApi');
export const bankingApi = createApiClient('BankingApi');
export const searchApi = createApiClient('SearchApi');
export const chatbotApi = createApiClient('ChatbotApi');
